import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import {
  mapTask,
  taskCreateInputToDto,
  taskUpdateInputToWire,
} from '@/features/patient-record/mappers';
import { patientQueries } from '@/features/patient-record/queries';
import { queryKeys } from '@/query/keys';
import type {
  PatientTaskCreateInput,
  PatientTaskSummaryViewModel,
  PatientTaskUpdateInput,
  PatientTaskViewModel,
} from '@/features/patient-record/types';

/**
 * Per-action task mutation hooks (PTV2-1097). Each owns its own
 * snapshot/rollback against `patientQueries.tasks(guid, { status: 'TODO' })`
 * — the panel's default view — so the row count + chip update instantly.
 * On settle the dedicated detail query (when present) and every status
 * variant of the list query are invalidated so the canonical server
 * fields (`completedAt`/`completedBy`, etc.) land.
 *
 * Update goes out as RFC 6902 JSON-Patch via `tasksApi.updatePersonTask`;
 * the wrapper composes the ops from a flat partial input.
 */

type Summaries = readonly PatientTaskSummaryViewModel[];

interface MutationContext {
  readonly previous: Summaries | undefined;
}

interface UseTasksCache {
  readonly queryKey: readonly unknown[];
  readonly snapshot: () => MutationContext;
  readonly setList: (next: Summaries) => void;
  readonly rollback: (ctx: MutationContext | undefined) => void;
  readonly settle: () => void;
}

function useTasksCache(guid: string): UseTasksCache {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.tasks(guid, { status: 'TODO' });

  const invalidateAllStatusVariants = (): void => {
    // Status filter is the last segment of the key — invalidate the prefix
    // so TODO / DONE / CANCELLED / ALL views all refetch.
    queryClient
      .invalidateQueries({ queryKey: queryKey.slice(0, -1) })
      .catch(() => undefined);
    // The My Cases inbox (PTV2-1236) shows the same tasks cross-patient and
    // shares these mutations, so refresh every one of its filter/page variants.
    queryClient
      .invalidateQueries({ queryKey: queryKeys.myCasesTasks })
      .catch(() => undefined);
  };

  return {
    queryKey,
    snapshot: () => ({ previous: queryClient.getQueryData<Summaries>(queryKey) }),
    setList: (next) => {
      queryClient.setQueryData<Summaries>(queryKey, next);
    },
    rollback: (ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData<Summaries>(queryKey, ctx.previous);
    },
    settle: invalidateAllStatusVariants,
  };
}

// ─── create ────────────────────────────────────────────────────────

export function useCreateTask(
  guid: string,
): UseMutationResult<PatientTaskViewModel | null, Error, PatientTaskCreateInput, MutationContext> {
  const cache = useTasksCache(guid);
  return useMutation({
    meta: { successMessage: 'success.toast.title.task_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.createPersonTask(guid, taskCreateInputToDto(input));
      return mapTask(dto, guid);
    },
    onMutate: (input) => {
      const ctx = cache.snapshot();
      const optimistic: PatientTaskSummaryViewModel = {
        id: `optimistic-${String(Date.now())}`,
        patientGuid: guid,
        type: input.type,
        title: input.title,
        assigneeId: input.assigneeId,
        priority: input.priority,
        status: 'TODO',
        dueDate: input.dueDate,
      };
      cache.setList([optimistic, ...(ctx.previous ?? [])]);
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: cache.settle,
  });
}

// ─── update ────────────────────────────────────────────────────────

interface UpdateVars {
  readonly taskId: string;
  readonly input: PatientTaskUpdateInput;
}

export function useUpdateTask(
  guid: string,
): UseMutationResult<PatientTaskViewModel | null, Error, UpdateVars, MutationContext> {
  const cache = useTasksCache(guid);
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'success.toast.title.task_updated' },
    mutationFn: async ({ taskId, input }) => {
      const dto = await apiClient.updatePersonTask(
        guid,
        taskId,
        taskUpdateInputToWire(input),
      );
      return mapTask(dto, guid);
    },
    onMutate: ({ taskId, input }) => {
      const ctx = cache.snapshot();
      const nextStatus = input.status;
      // Patch the row in-place; status transitions to DONE / CANCELLED
      // hide it from the TODO view, so filter accordingly.
      const next = (ctx.previous ?? []).flatMap((row) => {
        if (row.id !== taskId) return [row];
        const patched: PatientTaskSummaryViewModel = {
          ...row,
          ...(input.title !== undefined ? { title: input.title } : {}),
          ...(input.type !== undefined ? { type: input.type } : {}),
          ...(input.assigneeId !== undefined ? { assigneeId: input.assigneeId } : {}),
          ...(input.priority !== undefined ? { priority: input.priority } : {}),
          ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
          ...(nextStatus !== undefined ? { status: nextStatus } : {}),
        };
        return nextStatus !== undefined && nextStatus !== 'TODO' ? [] : [patched];
      });
      cache.setList(next);
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: (_data, _err, { taskId }) => {
      cache.settle();
      // Detail cache for the edited task should also refresh so the edit
      // dialog reopens with the canonical server state.
      const detailKey = patientQueries.task(guid, taskId).queryKey;
      queryClient.invalidateQueries({ queryKey: detailKey }).catch(() => undefined);
    },
  });
}

// ─── delete ────────────────────────────────────────────────────────

export function useDeleteTask(
  guid: string,
): UseMutationResult<undefined, Error, { readonly taskId: string }, MutationContext> {
  const cache = useTasksCache(guid);
  return useMutation({
    meta: { successMessage: 'success.toast.title.task_deleted' },
    mutationFn: async ({ taskId }) => apiClient.deletePersonTask(guid, taskId),
    onMutate: ({ taskId }) => {
      const ctx = cache.snapshot();
      cache.setList((ctx.previous ?? []).filter((row) => row.id !== taskId));
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: cache.settle,
  });
}
